// src/pages/AyudaPage.jsx
import React, { useState, forwardRef } from 'react';
import axiosInstance from '../axiosInstance';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Avatar,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide
} from '@mui/material';

// Iconos
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import GavelIcon from '@mui/icons-material/Gavel';
import CookieIcon from '@mui/icons-material/Cookie';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import LockIcon from '@mui/icons-material/Lock';
import ReplayIcon from '@mui/icons-material/Replay';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ShieldIcon from '@mui/icons-material/Shield';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const toWhatsAppLink = (phoneStr) => {
  const digits = (phoneStr || '').replace(/\D/g, '');
  return `https://wa.me/${digits}`;
};

const AyudaPage = () => {
  const [fadeIn, setFadeIn] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    mensaje: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Modales
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [openCookies, setOpenCookies] = useState(false);
  const [openPayPal, setOpenPayPal] = useState(false);

  // Función para manejar el cambio de tabs
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Función para copiar al portapapeles
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  // Función para manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación básica
    if (!formData.nombre || !formData.email || !formData.mensaje) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    // Validación de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setSubmitting(true);

    try {
      // Enviar datos al backend con axiosInstance (baseURL /api)
      const response = await axiosInstance.post('/contacto/', {
        nombre: formData.nombre,
        email: formData.email,
        mensaje: formData.mensaje,
        // Añadir información adicional útil
        fecha: new Date().toISOString(),
        pagina: 'Centro de Ayuda',
        userAgent: navigator.userAgent
      });

      if (response.data?.success) {
        toast.success('¡Mensaje enviado correctamente! Te responderemos a la brevedad.');
        setFormData({ nombre: '', email: '', mensaje: '' });
      } else {
        throw new Error(response.data?.message || 'Error al enviar el mensaje');
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      toast.error('Hubo un problema al enviar tu mensaje. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Función para manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Estilo común para texto justificado
  const justifiedText = { textAlign: 'justify' };

  // Números (normalizados para enlaces y copias)
  const soportoUSA = '+1 346 2529948';
  const soporteHND = '+504 9492-0119';
  const waUSA = toWhatsAppLink(soportoUSA);
  const telegramHND = `https://t.me/${soporteHND.replace(/\D/g, '')}`;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4, lg: 5 },
        py: 3,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Fade in={fadeIn} timeout={800}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: '24px',
            boxShadow: 'var(--mui-shadows-2)',
          }}
        >
          {/* Cabecera */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            mb: 4
          }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 56,
                height: 56,
                mr: { xs: 0, sm: 2 },
                mb: { xs: 2, sm: 0 },
                boxShadow: 'var(--mui-shadows-4)'
              }}
            >
              <HelpOutlineIcon fontSize="large" />
            </Avatar>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                Centro de Ayuda
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={justifiedText}>
                Encuentra información de contacto, políticas y respuestas a tus preguntas
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Tabs de navegación con animaciones */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="ayuda tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                },
                '& .MuiTab-root': {
                  transition: 'all 0.3s ease',
                  fontWeight: 'medium',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    fontWeight: 'bold',
                    transition: 'transform 0.2s ease',
                    transform: 'scale(1.05)',
                  }
                }
              }}
            >
              <Tab label="Contacto" id="tab-0"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'primary.main',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover::after': {
                    transform: 'translateX(0)',
                  },
                  '&.Mui-selected::after': {
                    transform: 'translateX(0)',
                  }
                }}
              />
              <Tab label="Políticas de Privacidad" id="tab-1"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'primary.main',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover::after': { transform: 'translateX(0)' },
                  '&.Mui-selected::after': { transform: 'translateX(0)' }
                }}
              />
              <Tab label="Términos y Condiciones" id="tab-2"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'primary.main',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover::after': { transform: 'translateX(0)' },
                  '&.Mui-selected::after': { transform: 'translateX(0)' }
                }}
              />
              <Tab label="Uso de Cookies" id="tab-3"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'primary.main',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover::after': { transform: 'translateX(0)' },
                  '&.Mui-selected::after': { transform: 'translateX(0)' }
                }}
              />
              <Tab label="Pagos con PayPal" id="tab-4"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'primary.main',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover::after': { transform: 'translateX(0)' },
                  '&.Mui-selected::after': { transform: 'translateX(0)' }
                }}
              />
            </Tabs>
          </Box>

          {/* Contenido de las tabs */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {/* Tarjeta: Correo */}
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 'var(--mui-shadows-8)',
                      borderColor: 'primary.main',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: 'primary.main',
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover::before': {
                      transform: 'scaleX(1)',
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.container', color: 'primary.main', mr: 1.5 }}>
                        <EmailIcon />
                      </Avatar>
                      <Typography variant="h6">Correo Electrónico</Typography>
                    </Box>
                    <Typography variant="body1" paragraph sx={justifiedText}>
                      Para consultas generales:
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'action.hover',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'primary.softBg',
                      }
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                        info@app-medifyhc.com
                      </Typography>
                      <Tooltip title="Copiar">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard('info@app-medifyhc.com')}
                          sx={{
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body1" paragraph sx={justifiedText}>
                      Para asuntos técnicos:
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'action.hover',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'primary.softBg',
                      }
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                        soporte@app-medifyhc.com
                      </Typography>
                      <Tooltip title="Copiar">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard('soporte@app-medifyhc.com')}
                          sx={{
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      startIcon={<EmailIcon />}
                      href="mailto:info@app-medifyhc.com"
                      variant="tonal"
                      sx={{
                        borderRadius: '20px',
                        px: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      Enviar correo
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Tarjeta: WhatsApp */}
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 'var(--mui-shadows-8)',
                      borderColor: 'success.main',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: 'success.main',
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover::before': {
                      transform: 'scaleX(1)',
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.container', color: 'success.main', mr: 1.5 }}>
                        <WhatsAppIcon />
                      </Avatar>
                      <Typography variant="h6">WhatsApp</Typography>
                    </Box>

                    <Typography variant="body1" paragraph sx={justifiedText}>
                      Soporte técnico:
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'action.hover',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'success.softBg',
                      }
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                        {soportoUSA}
                      </Typography>
                      <Tooltip title="WhatsApp">
                        <IconButton
                          size="small"
                          component="a"
                          href={waUSA}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'success.main',
                              color: 'white',
                            }
                          }}
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(soportoUSA.replace(/\s|-/g, ''))}
                          sx={{
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'success.main',
                              color: 'white',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Typography variant="body1" paragraph sx={justifiedText}>
                      Atención al cliente:
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'action.hover',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'success.softBg',
                      }
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                        {soporteHND}
                      </Typography>
                      <Tooltip title="Telegram">
                        <IconButton
                          size="small"
                          component="a"
                          href={telegramHND}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: '#0088cc',
                              color: 'white',
                            }
                          }}
                        >
                          <TelegramIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(soporteHND.replace(/\s|-/g, ''))}
                          sx={{
                            ml: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'success.main',
                              color: 'white',
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      startIcon={<WhatsAppIcon />}
                      href={waUSA}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="tonal"
                      color="success"
                      sx={{
                        borderRadius: '20px',
                        px: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      Abrir WhatsApp
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Formulario de contacto a todo lo ancho */}
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-8)',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: 'linear-gradient(90deg, #1976d2, #2e7d32)',
                      transform: 'scaleX(0)',
                      transformOrigin: 'center',
                      transition: 'transform 0.4s ease',
                    },
                    '&:hover::after': {
                      transform: 'scaleX(1)',
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      pb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Avatar sx={{ bgcolor: 'info.container', color: 'info.main', mr: 1.5 }}>
                        <SendIcon />
                      </Avatar>
                      <Typography variant="h6">Formulario de Contacto</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph sx={justifiedText}>
                      Completa el siguiente formulario y nos pondremos en contacto contigo lo antes posible. Todos los campos son obligatorios.
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                      <Grid container spacing={3}>
                        {/* Campos del formulario */}
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Nombre"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleChange}
                            required
                            disabled={submitting}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                transition: 'all 0.3s ease',
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.2)',
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={submitting}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                transition: 'all 0.3s ease',
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.2)',
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Mensaje"
                            name="mensaje"
                            multiline
                            rows={4}
                            value={formData.mensaje}
                            onChange={handleChange}
                            required
                            disabled={submitting}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                transition: 'all 0.3s ease',
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.2)',
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{
                              mt: 1,
                              borderRadius: '16px',
                              py: 1.2,
                              px: 4,
                              boxShadow: 'var(--mui-shadows-3)',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: 'rgba(255, 255, 255, 0.1)',
                                transform: 'translateX(-100%)',
                                transition: 'transform 0.4s ease',
                              },
                              '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: 'var(--mui-shadows-6)',
                              },
                              '&:hover::before': {
                                transform: 'translateX(100%)',
                              },
                              '&:active': {
                                transform: 'translateY(-1px)',
                                boxShadow: 'var(--mui-shadows-4)',
                              }
                            }}
                          >
                            {submitting ? 'Enviando...' : 'Enviar Mensaje'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Políticas de Privacidad - Card + Modal */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 'var(--mui-shadows-6)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Avatar
                        sx={{ bgcolor: 'primary.container', color: 'primary.main', mr: 1.5 }}
                      >
                        <PrivacyTipIcon />
                      </Avatar>
                      <Typography variant="h6">Política de Privacidad</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={justifiedText}>
                      Conoce cómo protegemos tus datos personales y los de tus pacientes.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setOpenPrivacy(true)}
                      endIcon={<OpenInNewIcon />}
                    >
                      Ver política
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            {/* Modal Privacidad */}
            <Dialog
              open={openPrivacy}
              onClose={() => setOpenPrivacy(false)}
              TransitionComponent={Transition}
              fullWidth
              maxWidth="md"
              PaperProps={{ sx: { borderRadius: '20px' } }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.container', color: 'primary.main' }}>
                  <PrivacyTipIcon />
                </Avatar>
                <Typography variant="h6" component="div">Política de Privacidad</Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    POLÍTICA DE PROTECCIÓN DE DATOS PERSONALES DE MEDIFY HEALTHCARE
                  </Typography>
                </Box>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">1. Nuestro compromiso con tu privacidad</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      En MEDIFY HealthCare, la protección de datos personales es una prioridad fundamental. Nos comprometemos a salvaguardar la confidencialidad y seguridad de la información personal de nuestros profesionales médicos y, especialmente, de los datos sensibles de sus pacientes.
                    </Typography>
                    <Typography paragraph sx={justifiedText}>
                      Cumplimos rigurosamente con todas las normativas vigentes en materia de protección de datos personales y exhortamos a todos los usuarios de nuestra plataforma a respetar las obligaciones de confidencialidad establecidas en las leyes de Ejercicio de la Medicina, Protección de Datos Personales y Derechos del Paciente.
                    </Typography>
                    <Typography sx={justifiedText}>
                      Esta política describe cómo recopilamos, utilizamos y protegemos la información que nos proporcionas al utilizar nuestros servicios.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                {/* Puedes seguir agregando más acordeones aquí como en tu versión anterior */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 3, fontStyle: 'italic', textAlign: 'right' }}
                >
                  Última actualización: 5 de agosto de 2025
                </Typography>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setOpenPrivacy(false)} variant="outlined">Cerrar</Button>
              </DialogActions>
            </Dialog>
          </TabPanel>

          {/* Términos y Condiciones - Card + Modal */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 'var(--mui-shadows-6)',
                      borderColor: 'secondary.main',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Avatar
                        sx={{ bgcolor: 'secondary.container', color: 'secondary.main', mr: 1.5 }}
                      >
                        <GavelIcon />
                      </Avatar>
                      <Typography variant="h6">Términos y Condiciones</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={justifiedText}>
                      Revisa las reglas de uso, propiedad intelectual y limitaciones de responsabilidad.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setOpenTerms(true)}
                      endIcon={<OpenInNewIcon />}
                    >
                      Ver términos
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            {/* Modal Términos */}
            <Dialog
              open={openTerms}
              onClose={() => setOpenTerms(false)}
              TransitionComponent={Transition}
              fullWidth
              maxWidth="md"
              PaperProps={{ sx: { borderRadius: '20px' } }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'secondary.container', color: 'secondary.main' }}>
                  <GavelIcon />
                </Avatar>
                <Typography variant="h6" component="div">Términos y Condiciones</Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body1" paragraph sx={justifiedText}>
                  Última actualización: 5 de agosto de 2025
                </Typography>

                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    ...justifiedText,
                    backgroundColor: 'action.hover',
                    p: 2,
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                      transform: 'scale(1.01)',
                    }
                  }}
                >
                  Al utilizar la plataforma MEDIFY HealthCare, usted acepta estos términos y condiciones en su totalidad. Si no está de acuerdo con estos términos, no debe utilizar nuestro sitio web y servicios.
                </Typography>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'secondary.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">1. Uso del servicio</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Al registrarse y utilizar MEDIFY HealthCare, usted acepta:
                    </Typography>
                    <ul>
                      <li><Typography sx={justifiedText}>Proporcionar información precisa y completa</Typography></li>
                      <li><Typography sx={justifiedText}>Mantener la confidencialidad de su cuenta y contraseña</Typography></li>
                      <li><Typography sx={justifiedText}>No utilizar el servicio para fines ilegales o no autorizados</Typography></li>
                      <li><Typography sx={justifiedText}>No interferir con la seguridad o funcionalidad de la plataforma</Typography></li>
                    </ul>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'secondary.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">2. Contenido médico</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      La información médica proporcionada en nuestra plataforma:
                    </Typography>
                    <ul>
                      <li><Typography sx={justifiedText}>Es solo para fines informativos generales</Typography></li>
                      <li><Typography sx={justifiedText}>No constituye consejo médico profesional</Typography></li>
                      <li><Typography sx={justifiedText}>No reemplaza la consulta con un profesional de la salud calificado</Typography></li>
                    </ul>
                    <Typography sx={justifiedText}>
                      Siempre busque el consejo de su médico u otro proveedor de salud calificado con cualquier pregunta que pueda tener sobre una condición médica.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'secondary.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">3. Propiedad intelectual</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Todo el contenido incluido en MEDIFY HealthCare, como texto, gráficos, logotipos, iconos, imágenes, clips de audio, descargas digitales y compilaciones de datos, es propiedad de MEDIFY HealthCare o sus proveedores de contenido y está protegido por las leyes internacionales de derechos de autor.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 0,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'secondary.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">4. Limitación de responsabilidad</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      MEDIFY HealthCare no será responsable por:
                    </Typography>
                    <ul>
                      <li><Typography sx={justifiedText}>Daños directos, indirectos, incidentales, consecuentes o punitivos</Typography></li>
                      <li><Typography sx={justifiedText}>Pérdida de datos, ingresos o ganancias</Typography></li>
                      <li><Typography sx={justifiedText}>Interrupciones del servicio o fallos técnicos</Typography></li>
                      <li><Typography sx={justifiedText}>Información inexacta proporcionada por usuarios</Typography></li>
                    </ul>
                  </AccordionDetails>
                </Accordion>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setOpenTerms(false)} variant="outlined">Cerrar</Button>
              </DialogActions>
            </Dialog>
          </TabPanel>

          {/* Política de Cookies - Card + Modal */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 'var(--mui-shadows-6)',
                      borderColor: 'warning.main',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Avatar
                        sx={{ bgcolor: 'warning.container', color: 'warning.main', mr: 1.5 }}
                      >
                        <CookieIcon />
                      </Avatar>
                      <Typography variant="h6">Política de Cookies</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={justifiedText}>
                      Aprende qué cookies usamos, por qué y cómo controlarlas.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setOpenCookies(true)}
                      endIcon={<OpenInNewIcon />}
                    >
                      Ver política
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            {/* Modal Cookies */}
            <Dialog
              open={openCookies}
              onClose={() => setOpenCookies(false)}
              TransitionComponent={Transition}
              fullWidth
              maxWidth="md"
              PaperProps={{ sx: { borderRadius: '20px' } }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'warning.container', color: 'warning.main' }}>
                  <CookieIcon />
                </Avatar>
                <Typography variant="h6" component="div">Política de Cookies</Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body1" paragraph sx={justifiedText}>
                  Última actualización: 5 de agosto de 2025
                </Typography>

                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    ...justifiedText,
                    backgroundColor: 'warning.softBg',
                    p: 2,
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'warning.softHoverBg',
                      transform: 'scale(1.01)',
                    }
                  }}
                >
                  Esta Política de Cookies explica cómo MEDIFY HealthCare utiliza cookies y tecnologías similares para reconocerlo cuando visita nuestro sitio web y aplicación. Explica qué son estas tecnologías y por qué las usamos, así como sus derechos para controlarlas.
                </Typography>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'warning.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">1. ¿Qué son las cookies?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Las cookies son pequeños archivos de datos que se colocan en su computadora o dispositivo móvil cuando visita un sitio web. Las cookies se utilizan ampliamente para hacer que los sitios web funcionen, o funcionen de manera más eficiente, así como para proporcionar información a los propietarios del sitio.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'warning.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">2. Tipos de cookies que utilizamos</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Utilizamos los siguientes tipos de cookies:
                    </Typography>
                    <ul>
                      <li><Typography sx={justifiedText}><strong>Cookies esenciales:</strong> Necesarias para el funcionamiento básico del sitio</Typography></li>
                      <li><Typography sx={justifiedText}><strong>Cookies de preferencias:</strong> Permiten recordar información que cambia la forma en que se comporta o se ve el sitio</Typography></li>
                      <li><Typography sx={justifiedText}><strong>Cookies estadísticas:</strong> Nos ayudan a entender cómo los visitantes interactúan con el sitio</Typography></li>
                      <li><Typography sx={justifiedText}><strong>Cookies de marketing:</strong> Utilizadas para rastrear a los visitantes en los sitios web</Typography></li>
                    </ul>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'warning.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">3. Control de cookies</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Puede configurar su navegador para rechazar todas o algunas cookies, o para alertarle cuando los sitios web establecen o acceden a las cookies. Si deshabilita o rechaza las cookies, tenga en cuenta que algunas partes de este sitio web pueden volverse inaccesibles o no funcionar correctamente.
                    </Typography>
                    <Typography paragraph sx={justifiedText}>
                      Para obtener más información sobre cómo controlar las cookies en su navegador, visite la sección de ayuda de su navegador web.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'warning.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">4. Cookies de terceros</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Algunos de nuestros socios comerciales pueden utilizar cookies en nuestro sitio web para diversos fines. Estos incluyen:
                    </Typography>
                    <ul>
                      <li><Typography sx={justifiedText}>Servicios de análisis (como Google Analytics)</Typography></li>
                      <li><Typography sx={justifiedText}>Proveedores de servicios de pago</Typography></li>
                      <li><Typography sx={justifiedText}>Plataformas de redes sociales</Typography></li>
                    </ul>
                    <Typography sx={justifiedText}>
                      No tenemos acceso ni control sobre estas cookies. Esta política de privacidad cubre el uso de cookies por parte de MEDIFY HealthCare y no cubre el uso de cookies por parte de terceros.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 0,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'warning.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">5. Actualización de nuestra política de cookies</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph sx={justifiedText}>
                      Podemos actualizar esta política de cookies periódicamente para reflejar cambios en las tecnologías, regulaciones o nuestras prácticas de negocio. Le recomendamos que revise esta política regularmente para mantenerse informado sobre cómo utilizamos las cookies.
                    </Typography>
                    <Typography sx={justifiedText}>
                      Si realizamos cambios significativos, le notificaremos a través de un aviso visible en nuestro sitio web o mediante un correo electrónico directo.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setOpenCookies(false)} variant="outlined">Cerrar</Button>
              </DialogActions>
            </Dialog>
          </TabPanel>

          {/* Pagos con PayPal - Card + Modal */}
          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 'var(--mui-shadows-6)',
                      borderColor: 'success.main',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'success.container', color: 'success.main', mr: 1.5 }}>
                        <PaymentIcon />
                      </Avatar>
                      <Typography variant="h6">Pagos con PayPal</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={justifiedText}>
                      Todo lo que debes saber sobre cómo procesamos pagos a través de PayPal en MEDIFY HealthCare.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => setOpenPayPal(true)}
                      endIcon={<OpenInNewIcon />}
                    >
                      Ver políticas de PayPal
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            {/* Modal PayPal */}
            <Dialog
              open={openPayPal}
              onClose={() => setOpenPayPal(false)}
              TransitionComponent={Transition}
              fullWidth
              maxWidth="md"
              PaperProps={{ sx: { borderRadius: '20px' } }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'success.container', color: 'success.main' }}>
                  <PaymentIcon />
                </Avatar>
                <Typography variant="h6" component="div">Políticas de Pagos con PayPal</Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    ...justifiedText,
                    backgroundColor: 'action.hover',
                    p: 2,
                    borderRadius: '16px',
                  }}
                >
                  Importante: Para recibir pagos, el correo de PayPal debe pertenecer a una cuenta PayPal Business.
                </Typography>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'success.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" color="success" />
                      <Typography fontWeight="medium">1. Requisitos de Cuenta</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={justifiedText}>
                      - Es obligatorio usar una cuenta PayPal Business para recibir pagos.<br />
                      - El correo configurado debe coincidir con el de la cuenta Business y estar verificado en PayPal.<br />
                      - MEDIFY HealthCare no crea ni administra cuentas de PayPal por ti.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'success.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonetizationOnIcon fontSize="small" color="success" />
                      <Typography fontWeight="medium">2. Comisiones y Tarifas</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={justifiedText}>
                      - Las comisiones cobradas por PayPal se aplican según su tabla vigente y pueden variar por país y moneda.<br />
                      - MEDIFY HealthCare no retiene comisiones adicionales por el uso de PayPal, salvo lo expresamente indicado en contratos o planes.<br />
                      - Consulta siempre las <Link href="https://www.paypal.com/" target="_blank" rel="noopener noreferrer">tarifas oficiales en PayPal</Link>.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'success.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReplayIcon fontSize="small" color="success" />
                      <Typography fontWeight="medium">3. Reembolsos y Disputas</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={justifiedText}>
                      - Los reembolsos se gestionan de acuerdo con las políticas de PayPal y las políticas de cancelación del profesional/médico.<br />
                      - Las disputas (claims) se tramitan directamente en PayPal. MEDIFY HealthCare puede brindar soporte, pero no decide sobre su resolución.<br />
                      - Asegúrate de detallar tus políticas de cancelación y reprogramación para tus pacientes.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'success.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CurrencyExchangeIcon fontSize="small" color="success" />
                      <Typography fontWeight="medium">4. Moneda, Precios y Liquidaciones</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={justifiedText}>
                      - La moneda y el precio de consulta los defines en tu panel de configuración.<br />
                      - PayPal realiza conversiones de moneda cuando aplica, con sus tipos de cambio y posibles comisiones.<br />
                      - Los fondos se liquidan en tu cuenta PayPal; desde allí puedes retirarlos según las opciones de tu país.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    mb: 2,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 'var(--mui-shadows-3)',
                      borderLeft: '4px solid',
                      borderColor: 'success.main',
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShieldIcon fontSize="small" color="success" />
                      <Typography fontWeight="medium">5. Seguridad y Cumplimiento</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={justifiedText}>
                      - PayPal es responsable del procesamiento del pago y del manejo de datos financieros conforme a sus certificaciones y normativas (PCI DSS, AML/KYC donde aplique).<br />
                      - MEDIFY HealthCare no almacena credenciales de tarjetas ni saldos de PayPal de usuarios.<br />
                      - Cumple con las leyes de tu país respecto a facturación, impuestos y prevención de lavado de activos.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Nota: Esta información es de carácter informativo y no constituye asesoramiento legal ni fiscal. Revisa siempre las políticas actualizadas en PayPal y consulta con tus asesores.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button
                  variant="text"
                  startIcon={<OpenInNewIcon />}
                  href="https://www.paypal.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ir a PayPal
                </Button>
                <Button onClick={() => setOpenPayPal(false)} variant="contained" color="success">
                  Cerrar
                </Button>
              </DialogActions>
            </Dialog>
          </TabPanel>
        </Paper>
      </Fade>
    </Box>
  );
};

// Componente auxiliar para las tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default AyudaPage;
